<template>
    <fieldset class="c-radio-group"
              :class="{ 'has-validation-error': error }">
        <legend v-if="label"
                class="c-radio-group__legend">
            {{ label }}
        </legend>
        <slot/>
        <div v-if="error"
             role="alert">
            <ol>
                <li class="text-red-500 text-12 font-medium pb-5">
                    {{ error }}
                </li>
            </ol>
        </div>
    </fieldset>
</template>

<script setup lang="ts">
import { computed, provide } from 'vue';
import useFieldValidation from '@/project/form/useFieldValidation';
import { radioButtonInjectionKey, type FormRadioButtonContext } from './formTypes';
import dictionaryService from '@/core/translation/dictionary.service';

const props = withDefaults(defineProps<{
    name: string;
    modelValue?: string | number | boolean | object | null;
    label?: string;
    required?: boolean;
    errorLabelKey?: string;
}>(), {
    modelValue: null,
    label: undefined,
    required: false,
    errorLabelKey: 'Validation.Required',
});

const model = defineModel<string | number | boolean | object | null>();

const validator = () => {
    if (!props.required) return null;
    if (model.value === null || model.value === undefined || model.value === false || model.value === '') {
        return dictionaryService.get(props.errorLabelKey);
    }
    return null;
};

const { field, validate, setError } = useFieldValidation(
    props.name,
    props.label ?? props.name,
    validator,
    computed(() => model.value),
    { mode: 'lazy' },
    undefined,
    'radio',
);

const error = computed(() => field.error);

async function validateGroup(): Promise<void> {
    await validate();
}

defineExpose({ validate, field, setError });

provide<FormRadioButtonContext>(radioButtonInjectionKey, {
    get modelValue() {
        return model.value;
    },
    updateValue(value: string | number | boolean | object) {
        model.value = value;
    },
    validateGroup,
});
</script>

<style lang="less" scoped>
.c-radio-group {
    border: none;
    margin: 0;
    padding: 0;
}

.c-radio-group__legend {
    @apply font-medium mb-5;
}
</style>
